/**
 * Department settings — admin overrides per department:
 *   { [departmentName]: { rename?: string|null, leadId?: string|null } }
 *
 * `rename` relabels the department at roster-build time (renaming to an
 * existing department's name MERGES them — handy for duplicates like
 * "IT" vs "Information Technology"), and survives AD re-syncs the same way
 * exclusion rules do. `leadId` pins the department card's "Led by" person.
 *
 * Server-backed when VITE_API_BASE_URL is set; localStorage fallback in dev.
 */

const KEY = 'peoplevue_dept_settings';
const API_BASE = import.meta.env.VITE_API_BASE_URL;

let apiToken = null;

function readLocal() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
}

function writeLocal(map) {
  try { localStorage.setItem(KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

/** Hydrate from the server. Call once after sign-in. Non-fatal on failure. */
export async function syncDeptSettings(token) {
  if (!API_BASE || !token) return false;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/department-settings`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Department settings API ${res.status}`);
    writeLocal(await res.json() || {});
    return true;
  } catch (err) {
    console.warn('Failed to sync department settings (non-fatal):', err.message);
    return false;
  }
}

/** Full settings map (synchronous read from the cache). */
export function getDeptSettings() {
  return readLocal();
}

/**
 * rename map keyed by NORMALIZED original name (trimmed, lowercased) —
 * applied when building the roster. Normalized matching catches the
 * whitespace/case variants that AD data inevitably contains ("IT", "It ", …).
 */
export function getDeptRenames() {
  const settings = readLocal();
  const renames = {};
  Object.entries(settings).forEach(([dept, s]) => {
    if (s?.rename && s.rename.trim().toLowerCase() !== dept.trim().toLowerCase()) {
      renames[dept.trim().toLowerCase()] = s.rename;
    }
  });
  return renames;
}

/**
 * Save settings for one department (admin-gated server-side). Passing
 * rename: null / leadId: null clears that override.
 *
 * Local-first like every other service: the local rule always saves (and
 * takes effect immediately); the server push is non-fatal so an undeployed
 * or unreachable endpoint never blocks the admin. Once the endpoint is live,
 * pushes persist the rule for everyone.
 */
export async function saveDeptSettings(dept, { rename = null, leadId = null }) {
  const map = readLocal();
  if (!rename && !leadId) {
    delete map[dept];
  } else {
    map[dept] = { ...(map[dept] || {}), rename: rename || null, leadId: leadId || null };
  }
  writeLocal(map);

  if (API_BASE && apiToken) {
    fetch(`${API_BASE}/department-settings/${encodeURIComponent(dept)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
      body: JSON.stringify({ rename, leadId }),
    })
      .then((res) => { if (!res.ok) console.warn(`Department settings push failed: ${res.status}`); })
      .catch((err) => console.warn('Department settings push failed:', err.message));
  }
  return true;
}
